// Single-instance, identity-bound loader for the Guru Cloud AI feedback widget.
//
// The widget script (guru-widget.js) is a self-contained IIFE that, at init,
// reads `window.GuruWidgetMetadata` ONCE to compute its notification identity
// hash (server token config: identity_keys=["user_id"]) and its segment
// hashes (scopes / persona / app_section), then polls the notifications
// endpoint with those frozen hashes every 5s. It exposes no destroy or
// re-register API — removing the <script> tag does NOT stop a live instance
// (its poll timer keeps running and its toasts re-create their own DOM).
//
// Two hard rules follow, both enforced here:
//
// 1. Never load the script before an authenticated user is in
//    `window.GuruWidgetMetadata`. An instance initialized without a user_id
//    registers this browser as "__anonymous__" permanently; anonymous-bucket
//    completion notifications are then delivered to every such browser and
//    first-ack consumes them — the notification "bleed-over" bug.
//
// 2. Never load a second instance in the same page JS lifetime. The widget is
//    bound to the first authenticated user; if a different user id shows up
//    later (impersonation, re-login in the same tab), callers get
//    'identity-mismatch' and must keep the widget UI hidden. A page reload
//    gives the new identity a fresh instance. Full in-tab identity switching
//    needs a platform-side destroy/re-register API on the widget.

export type GuruWidgetStatus = 'active' | 'identity-mismatch' | 'no-user';

export const GURU_WIDGET_TOKEN = 'wt_TdOlXQtX5tbRBtmdxooXmgGHnrp0NCZ5T0u-lJOBqic';

const SCRIPT_ID = 'guru-widget-script';
const HIDE_STYLE_ID = 'guru-widget-hide-style';
const WIDGET_SCRIPT_URL = 'https://www.gurucloudai.com/static/js/guru-widget.js';

let _boundUserId: string | null = null;

// Components observe the binding via useSyncExternalStore.
const _listeners = new Set<() => void>();

function _setBoundUserId(userId: string | null): void {
  if (_boundUserId === userId) return;
  _boundUserId = userId;
  _listeners.forEach((listener) => listener());
}

/** Subscribe to binding changes; returns an unsubscribe function. */
export function subscribeGuruWidget(listener: () => void): () => void {
  _listeners.add(listener);
  return () => {
    _listeners.delete(listener);
  };
}

export interface GuruWidgetOptions {
  color: string;
  title: string;
}

/**
 * Load the Guru widget for the given authenticated user, or report why not.
 *
 * The caller MUST have already synced `window.GuruWidgetMetadata` for this
 * user (see syncGuruWidgetMetadata) so the widget's async init reads a
 * fully-identified metadata object.
 */
export function ensureGuruWidget(
  userId: string | null | undefined,
  options: GuruWidgetOptions,
): GuruWidgetStatus {
  if (!userId) return 'no-user';

  const existing = document.getElementById(SCRIPT_ID);
  if (existing) {
    if (_boundUserId === null) {
      // Script present but module state lost (e.g. HMR): adopt the instance.
      _setBoundUserId(userId);
    }
    return _boundUserId === userId ? 'active' : 'identity-mismatch';
  }

  const script = document.createElement('script');
  script.id = SCRIPT_ID;
  script.src = WIDGET_SCRIPT_URL;
  script.async = true;
  script.setAttribute('data-token', GURU_WIDGET_TOKEN);
  script.setAttribute('data-color', options.color);
  script.setAttribute('data-title', options.title);
  script.setAttribute('data-position', 'bottom-right');
  document.body.appendChild(script);
  _setBoundUserId(userId);
  return 'active';
}

/**
 * Show/hide the widget's native launcher button via a persistent style rule.
 * A style tag (not element.style) because the widget re-shows its button when
 * the modal closes; programmatic .click() still works on display:none.
 */
export function setGuruNativeButtonHidden(hidden: boolean): void {
  const existing = document.getElementById(HIDE_STYLE_ID);
  if (hidden) {
    if (existing) return;
    const style = document.createElement('style');
    style.id = HIDE_STYLE_ID;
    style.textContent = '#guru-widget-btn { display: none !important; }';
    document.head.appendChild(style);
  } else {
    existing?.remove();
  }
}

/** The user id the live widget instance is bound to, if any. */
export function getGuruWidgetBoundUserId(): string | null {
  return _boundUserId;
}

/** Test-only: clear module state and remove injected DOM. */
export function _resetGuruWidgetForTests(): void {
  _setBoundUserId(null);
  document.getElementById(SCRIPT_ID)?.remove();
  document.getElementById(HIDE_STYLE_ID)?.remove();
}
